/**
 * Inventory Drawer Component
 *
 * Side drawer for viewing inventory details.
 * @see US-12.6 - Page Liste
 */

import { useMemo, useState } from 'react';
import { User, AlertTriangle, ChevronRight } from 'lucide-react';
import { Drawer, Badge, Skeleton, Button } from '@/components/ui';
import { useCurrency, useFormatDate } from '@/hooks';
import { formatCentimes, formatQuantity } from '@/lib/format';
import { useInventory } from '../hooks/useInventories';
import { DocumentSection } from '@/features/documents';
import {
  InventoryStatus,
  INVENTORY_STATUS_LABELS,
  INVENTORY_STATUS_COLORS,
  INVENTORY_TYPE_LABELS,
} from '@bstock/shared';

interface InventoryDrawerProps {
  inventoryId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit?: (inventoryId: string) => void;
  showFinancials?: boolean;
}

function getStatusVariant(status: InventoryStatus): 'success' | 'warning' | 'danger' | 'neutral' {
  const colorMap: Record<string, 'success' | 'warning' | 'danger' | 'neutral'> = {
    green: 'success',
    gray: 'neutral',
    orange: 'warning',
    red: 'danger',
  };
  return colorMap[INVENTORY_STATUS_COLORS[status]] ?? 'neutral';
}

export function InventoryDrawer({
  inventoryId,
  open,
  onOpenChange,
  onEdit,
  showFinancials = false,
}: InventoryDrawerProps) {
  const currency = useCurrency();
  const { formatDateTimeShort } = useFormatDate();
  const { data: inventory, isLoading } = useInventory(open ? inventoryId : null);
  const [expandedCategoryId, setExpandedCategoryId] = useState<string | null>(null);

  const canEdit = inventory && (
    inventory.status === InventoryStatus.DRAFT ||
    inventory.status === InventoryStatus.IN_PROGRESS
  );

  // Group product groups by category (Sprint 21b)
  const productGroupsByCategory = useMemo(() => {
    if (!inventory?.productGroups) return [];

    const grouped = new Map<
      string,
      { category: { id: string; name: string; code: string }; groups: typeof inventory.productGroups }
    >();

    for (const group of inventory.productGroups) {
      const catId = group.categoryId;
      if (!grouped.has(catId)) {
        grouped.set(catId, {
          category: { id: catId, name: group.categoryName, code: group.categoryCode },
          groups: [],
        });
      }
      grouped.get(catId)!.groups.push(group);
    }

    return Array.from(grouped.values());
  }, [inventory?.productGroups]);

  const footer = canEdit && onEdit && inventoryId ? (
    <div className="flex justify-end">
      <Button onClick={() => onEdit(inventoryId)}>
        {inventory?.status === InventoryStatus.DRAFT ? 'Commencer' : 'Continuer'}
      </Button>
    </div>
  ) : undefined;

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title={inventory?.inventoryNumber ?? 'Inventaire'}
      description={inventory?.name || (inventory?.type ? INVENTORY_TYPE_LABELS[inventory.type] : undefined)}
      size="xlarge"
      footer={footer}
    >
      <div>
          {isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-24" />
              <Skeleton className="h-48" />
            </div>
          ) : inventory ? (
            <div className="space-y-6">
              {/* Status and summary */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-neutral-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant={getStatusVariant(inventory.status)}>
                      {INVENTORY_STATUS_LABELS[inventory.status]}
                    </Badge>
                  </div>
                  <div className="text-sm text-neutral-500 space-y-1">
                    <div>Cree le {formatDateTimeShort(inventory.createdAt)}</div>
                    {inventory.validatedAt && (
                      <div>Valide le {formatDateTimeShort(inventory.validatedAt)}</div>
                    )}
                    {inventory.effectiveDate && (
                      <div className="text-primary-600">
                        Date d'effet : {formatDateTimeShort(inventory.effectiveDate)}
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      <span>
                        {inventory.createdByUser.firstName} {inventory.createdByUser.lastName}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-neutral-50 rounded-lg p-4">
                  <div className="text-sm font-medium text-neutral-700 mb-2">
                    Progression
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-3 bg-neutral-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary-500 rounded-full transition-all"
                        style={{ width: `${inventory.summary.progressPercent}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium text-dark-500">
                      {inventory.summary.progressPercent}%
                    </span>
                  </div>
                  <div className="text-xs text-neutral-500 mt-2">
                    {inventory.summary.countedLines} / {inventory.summary.totalLines} lignes comptees
                  </div>
                </div>
              </div>

              {/* Variance summary - MANAGER+ uniquement */}
              {showFinancials && inventory.summary.linesWithVariance > 0 && (
                <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-warning-700 mb-2">
                    <AlertTriangle className="w-5 h-5" />
                    <span className="font-medium">
                      {inventory.summary.linesWithVariance} ecart(s) detecte(s)
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-neutral-500">Excedent</div>
                      <div className="text-success-600 font-medium">
                        +{formatCentimes(inventory.summary.positiveVarianceValue, currency)}
                      </div>
                    </div>
                    <div>
                      <div className="text-neutral-500">Manquant</div>
                      <div className="text-danger-600 font-medium">
                        {formatCentimes(inventory.summary.negativeVarianceValue, currency)}
                      </div>
                    </div>
                    <div>
                      <div className="text-neutral-500">Net</div>
                      <div className={`font-medium ${
                        inventory.summary.netVarianceValue < 0 ? 'text-danger-600' : 'text-success-600'
                      }`}>
                        {inventory.summary.netVarianceValue >= 0 ? '+' : ''}
                        {formatCentimes(inventory.summary.netVarianceValue, currency)}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Lines by category (Sprint 21b: grouped by product) */}
              <div>
                <h3 className="text-sm font-medium text-neutral-700 mb-3">
                  Lignes d'inventaire
                </h3>
                <div className="space-y-4">
                  {productGroupsByCategory.map(({ category, groups }) => (
                    <div key={category.id} className="border border-neutral-200 rounded-lg overflow-hidden">
                      <button
                        type="button"
                        onClick={() =>
                          setExpandedCategoryId((prev) =>
                            prev === category.id ? null : category.id
                          )
                        }
                        aria-expanded={expandedCategoryId === category.id}
                        className="w-full bg-neutral-100 px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-neutral-200 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <ChevronRight
                            className={`w-4 h-4 text-neutral-500 transition-transform duration-150 ${
                              expandedCategoryId === category.id ? 'rotate-90' : ''
                            }`}
                          />
                          <span className="text-sm font-medium text-neutral-700">{category.name}</span>
                        </div>
                        <span className="text-sm text-neutral-500">
                          {groups.filter((g) => g.lines.some((l) => l.countedQty !== null)).length} / {groups.length}
                        </span>
                      </button>
                      {expandedCategoryId === category.id && <div className="divide-y divide-neutral-100">
                        {groups.map((group) => {
                          const isUncounted = !group.lines.some((l) => l.countedQty !== null);
                          const totalCountedQty = group.totalCountedQty;
                          const hasPackagingLines = group.lines.some((l) => l.packagingId !== null);

                          return (
                            <div
                              key={group.rawProductId}
                              className={`px-4 py-3 ${isUncounted ? 'bg-warning-50' : ''}`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className={`text-sm font-medium ${isUncounted ? 'text-warning-700' : 'text-dark-500'}`}>
                                      {group.rawProductName}
                                    </span>
                                    {isUncounted && (
                                      <Badge variant="warning" className="text-xs">Non compte</Badge>
                                    )}
                                  </div>
                                  <div className="text-xs text-neutral-500">
                                    {group.rawProductSku}
                                  </div>
                                </div>
                                <div className="text-right">
                                  <div className="flex items-center gap-4 text-sm">
                                    <div className="text-neutral-500">
                                      Theo: {group.theoreticalQty ?? '-'} {group.recipeUnitSymbol}
                                    </div>
                                    <div className={isUncounted ? 'text-warning-600 font-medium' : 'text-dark-500 font-medium'}>
                                      Compte: {isUncounted ? '-' : formatQuantity(totalCountedQty)} {group.recipeUnitSymbol}
                                    </div>
                                    {group.variance !== null && group.variance !== 0 && (
                                      <div className={group.variance < 0 ? 'text-danger-600' : 'text-success-600'}>
                                        {group.variance > 0 ? '+' : ''}{formatQuantity(group.variance)}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Detail packaging rows (Sprint 21b) */}
                              {hasPackagingLines && !isUncounted && (
                                <div className="mt-1 ml-4 space-y-0.5">
                                  {group.lines
                                    .filter((l) => l.packagingId !== null && l.packagingQty !== null && l.packagingQty > 0)
                                    .map((line) => (
                                      <div key={line.id} className="text-xs text-neutral-400">
                                        {line.packagingQty} x {line.packagingName} = {formatQuantity(line.countedQty ?? 0)} {group.recipeUnitSymbol}
                                      </div>
                                    ))}
                                  {(() => {
                                    const bulkLine = group.lines.find((l) => l.packagingId === null);
                                    if (bulkLine && bulkLine.countedQty !== null && bulkLine.countedQty > 0) {
                                      return (
                                        <div className="text-xs text-neutral-400">
                                          + {formatQuantity(bulkLine.countedQty)} {group.recipeUnitSymbol} vrac
                                        </div>
                                      );
                                    }
                                    return null;
                                  })()}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>}
                    </div>
                  ))}
                </div>
              </div>

              {/* Notes */}
              {inventory.notes && (
                <div>
                  <h3 className="text-sm font-medium text-neutral-700 mb-2">Notes</h3>
                  <div className="bg-neutral-50 rounded-lg p-4 text-sm text-neutral-600">
                    {inventory.notes}
                  </div>
                </div>
              )}

              {/* Validation info */}
              {inventory.validatedByUser && (
                <div className="text-sm text-neutral-500 border-t border-neutral-200 pt-4">
                  Valide par {inventory.validatedByUser.firstName} {inventory.validatedByUser.lastName}
                </div>
              )}

              {/* Documents (validated inventories) */}
              {inventory.status === InventoryStatus.VALIDATED && inventoryId && (
                <DocumentSection
                  entityRoute="inventories"
                  entityId={inventoryId}
                />
              )}
            </div>
          ) : (
            <div className="text-center text-neutral-500 py-8">
              Inventaire non trouve
            </div>
          )}
      </div>
    </Drawer>
  );
}
